/**
 * Rail icons, all drawn to the same settings: 24×24 viewBox, stroke-width 1.7, `currentColor`,
 * round caps and joins. They inherit colour from the rail item, so hover and active states need no
 * per-icon handling.
 *
 * Inline rather than files in /public: an `<img>` cannot inherit `currentColor`, so a file-based
 * icon would need a separate copy per state or a CSS mask. Two of these come from the design
 * handoff (`icon-page-star.svg`, `icon-seek-compass.svg`); the rest match lucide at the same
 * settings, which is what the handoff specifies for the others.
 *
 * Rendered at 21px in the rail. The handoff's floor of 20px is why the "new matter" star has FOUR
 * points rather than five — a five-point star loses its spikes at this size.
 */

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
};

function Icon({ size = 21, children, ...rest }) {
  return (
    <svg {...base} {...rest} width={size} height={size}>
      {children}
    </svg>
  );
}

/** lucide: LayoutList — a list of records. */
export function IconMyMatters(props) {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="7" height="7" rx="1.5" />
      <rect x="3" y="13" width="7" height="7" rx="1.5" />
      <path d="M14 6.5h7M14 10h5M14 15.5h7M14 19h5" />
    </Icon>
  );
}

/** From the handoff: a page with a four-point star — creating a new record. */
export function IconNewMatter(props) {
  return (
    <Icon {...props}>
      <path d="M13.5 3.5H6.5A1.5 1.5 0 0 0 5 5v14a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V9z" />
      <path d="M13.5 3.5V9H19" />
      <path d="M12 11.5l1.25 2.75L16 15.5l-2.75 1.25L12 19.5l-1.25-2.75L8 15.5l2.75-1.25z" />
    </Icon>
  );
}

/** lucide: CheckCircle — approvals waiting on you. */
export function IconApprovals(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </Icon>
  );
}

/** lucide: FileText — saved templates. */
export function IconTemplates(props) {
  return (
    <Icon {...props}>
      <path d="M13.5 3.5H6.5A1.5 1.5 0 0 0 5 5v14a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V9z" />
      <path d="M13.5 3.5V9H19" />
      <path d="M8.5 13h7M8.5 16.5h4.5" />
    </Icon>
  );
}

/** lucide: Hash — client and matter numbers. */
export function IconNumbering(props) {
  return (
    <Icon {...props}>
      <path d="M5 9.5h14M5 14.5h14M10 4.5 8.5 19.5M15.5 4.5 14 19.5" />
    </Icon>
  );
}

/** lucide: GitBranch — where a matter has reached in its chain. */
export function IconRouting(props) {
  return (
    <Icon {...props}>
      <circle cx="7" cy="5.5" r="2" />
      <circle cx="7" cy="18.5" r="2" />
      <circle cx="17" cy="12" r="2" />
      <path d="M7 7.5v9M9 18.5h3a3 3 0 0 0 3-3v-1.5" />
    </Icon>
  );
}

/** lucide: Settings — administration. */
export function IconAdmin(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.1-1.56 1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.88 1.7 1.7 0 0 0-1.56-1H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.65 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.56V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.56 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1z" />
    </Icon>
  );
}

/** lucide: CircleHelp — the written guide and the walkthrough. */
export function IconHelp(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </Icon>
  );
}
